import type { EventApproval, EventDoc } from "../types/event.ts";
import { decodeEntities } from "./htmlEntities.ts";
import { isEmptyRichText } from "./richText.ts";

/* The portal's `permissions.*` strings, English. `medicalDetails`,
   `areCorrect` and `termsHeader` are keys it asks for (calendar.medical-details,
   calendar.are-correct, calendar.term-and-conditions) that are not in its
   locale files, so their wording here is unverified. `medicalAuthority`,
   `decline`, `approveNow`, `medicalInfo`, `externalNotice`, `dietaryQuestion`,
   `view` and `noResponse` are defaults written in its source. `dietaryTitle`
   (permissions.dietary-requirements) is likewise a source default only: no
   dietary key is in parents.json. `cancel` and `close` are common.json's
   `button.*` strings. */
export const APPROVAL_TEXT = {
	approve: "Approve",
	decline: "Decline",
	termsLink: "Terms & Conditions",
	termsHeader: "Terms & Conditions",
	acceptTerms: "I accept Terms and Conditions",
	save: "Save",
	medicalDetails: "Medical details",
	areCorrect: " are correct",
	medicalAuthority: "Medical Authority",
	medicalInfo: "Medical Info",
	approveNow: "Approve Now",
	externalNotice:
		"As this is an external event, you will need to use our partner's permission forms",
	expiryWarning: (days: number) => `Event is less than ${days} days away.`,
	contactCollege: "Please contact the college for changes",
	dietaryQuestion: "Does your daughter have any dietary requirements?",
	dietaryTitle: "Dietary requirements",
	cancel: "Cancel",
	close: "Close",
	view: "View",
	noResponse: "No response",
};

/**
 * Whether an event has a permission card to preview.
 *
 * The same two switches the Permissions tab hangs everything else off:
 * `Required`, which opens the checks, reminders, lock days and external forms,
 * and `Compulsory`, which keeps the terms on screen on its own. Anything set
 * while neither is on is hidden by the tab and never reaches a parent, so it
 * does not count: a stale reminder on an event that no longer asks for
 * permission should not offer a card nobody will see.
 */
export function hasPermissionCard(approval: EventApproval | undefined): boolean {
	return approval?.Required === true || approval?.Compulsory === true;
}

/**
 * The people on a preview card. Invented, and said to be on the card itself.
 *
 * Never a real student or carer: the preview renders an event's own draft, and
 * the event document carries a roll of real attendees that this feature has no
 * business touching.
 */
export const SAMPLE_STUDENT = "Sample Student";
export const SAMPLE_YEAR = "Year 7";
export const SAMPLE_PARENT = "Sample Parent";

/**
 * What the permission card shows for an event, decided in one place so a test
 * can pin it. Every field is read from the draft; nothing here is invented.
 *
 * `Required` is the gate the Permissions tab itself uses: the checks, lock
 * days and external forms are hidden by the tab unless it is on, so anything
 * left set under them never reaches a parent and does not count here either.
 * Terms stand on their own (Compulsory alone keeps them on screen).
 */
export interface ApprovalCardModel {
	title: string;
	/** MyPymble's DateLong: weekday, month, day, in Sydney time. No year. */
	dateLabel: string | null;
	/** The venue row: `Location.Name`, else `Location.Address`, trimmed. */
	locationLabel: string | null;
	/**
	 * The card's summary text, as MyPymble's approvals card picks it: the long
	 * description, else the short, else the notes. Uncut; the card's CSS clamps
	 * it to three lines.
	 */
	descriptionHtml: string | null;
	/** The banner image, drawn as a square beside the title. */
	imageUrl: string | null;
	/** The event has started: MyPymble draws no action row for these. */
	past: boolean;
	/** The internal "medical details are correct" row. */
	medicalRow: boolean;
	termsHtml: string | null;
	/** The event asks for dietary requirements (`Checks.Dietary`): a link in the modal. */
	dietary: boolean;
	/** Red "Event is less than N days away" line; N is `lockDays`. */
	expiryWarning: boolean;
	lockDays: number | null;
	externalPermission: boolean;
	externalMedical: boolean;
	/** Decline is hidden when attendance is compulsory. */
	showDecline: boolean;
	/** The travel acknowledgement row, with the sentence beside its checkbox. */
	transportAck: { text: string } | null;
}

/** The portal's `app.approvalLockPeriod`, used when the event sets no lock days. */
const DEFAULT_LOCK_DAYS = 3;

const trimmed = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/** `college_bus` -> `College bus`, as the portal's summariseTransport spells a code. */
const spellOutCode = (code: string): string => {
	const spaced = code.replace(/[_-]+/g, " ").trim();
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * The acknowledgement the portal asks for (utils/eventTransport.ts): only an
 * explicit `AcknowledgementRequired: true`, and never for the "none" code.
 */
function buildTransportAck(
	transport: EventDoc["Transport"],
): { text: string } | null {
	if (!transport || transport.AcknowledgementRequired !== true) return null;
	const code = trimmed(transport.Code);
	if (code.toLowerCase() === "none") return null;
	const own = trimmed(transport.AcknowledgementText);
	if (own) return { text: own };
	const label =
		trimmed(transport.Label) || trimmed(transport.Description) || spellOutCode(code);
	return {
		text: label
			? `I acknowledge that my daughter will be travelling by ${label}`
			: "I acknowledge the travel arrangements for this event",
	};
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function buildApprovalCardModel(
	event: EventDoc,
	now: Date = new Date(),
): ApprovalCardModel {
	const approval = event.Approval;
	const required = approval?.Required === true;
	const start = event.StartDateTime ? new Date(event.StartDateTime) : null;
	const validStart = start && !Number.isNaN(start.getTime()) ? start : null;
	const past = validStart !== null && validStart.getTime() < now.getTime();

	const lock = approval?.RSVP?.LockDays;
	/* The portal falls back to its approval lock period when the event sets
	   none, so a Required event always has a window. */
	const lockDays = required
		? Number.isInteger(lock)
			? (lock as number)
			: DEFAULT_LOCK_DAYS
		: null;
	const externalPermission = required && !!approval?.ExternalPermissionForm;
	const externalMedical = required && !!approval?.ExternalMedicalForm;

	const descriptionHtml =
		[
			trimmed(event.Description?.Long),
			trimmed(event.Description?.Short),
			trimmed(event.AdditionalNotes),
		].find((v) => v !== "" && !isEmptyRichText(v)) ?? null;
	const terms = approval?.TermsAndConditions ?? "";

	return {
		title: decodeEntities(event.Title ?? "") || "Untitled event",
		dateLabel: validStart
			? new Intl.DateTimeFormat("en-AU", {
					weekday: "long",
					month: "long",
					day: "numeric",
					timeZone: "Australia/Sydney",
				}).format(validStart)
			: null,
		locationLabel:
			trimmed(event.Location?.Name) || trimmed(event.Location?.Address) || null,
		descriptionHtml,
		imageUrl: trimmed(event.Banner?.ImageUrl) || null,
		past,
		medicalRow: required && approval?.Checks?.Medical === true && !externalMedical,
		termsHtml: isEmptyRichText(terms) ? null : terms,
		dietary: required && approval?.Checks?.Dietary === true,
		expiryWarning:
			!past &&
			lockDays !== null &&
			validStart !== null &&
			(validStart.getTime() - now.getTime()) / DAY_MS <= lockDays,
		lockDays,
		externalPermission,
		externalMedical,
		showDecline: approval?.Compulsory !== true,
		transportAck: buildTransportAck(event.Transport),
	};
}

/**
 * What the event modal a carer opens from the card shows, on top of the card's
 * own fields. Read from the same draft; the extras are the ones the portal's
 * EventItemDetail draws that the card does not.
 */
export interface EventModalModel extends ApprovalCardModel {
	/** The event owner, from the draft's supervisors (see `buildEventModalModel`). Null when it carries none. */
	ownerName: string | null;
	/** The portal's TimeShort: "9:00 am - 3:00 pm". The end is omitted when there is none. */
	timeLabel: string | null;
	/** `Description.Long` when it differs from `Short`, else `Short`, as in EventItemDetail. */
	bodyHtml: string | null;
	/** The FULL `AdditionalNotes`, under its own heading. Never cut, unlike the card's. */
	additionalNotesHtml: string | null;
	/** What still blocks Approve, as its tooltip words it; empty when nothing does. */
	disabledReason: string;
	/** What blocks Decline: only the lock window, never the checks. */
	declineDisabledReason: string;
}

/** The portal's `permissions.*` wording for what blocks Approve, in its own order. */
const REASON_MEDICAL = (name: string) =>
	`Please confirm ${name}'s medical details before proceeding`;
const REASON_TERMS =
	"Please read and accept the terms and conditions before proceeding";
const REASON_TRANSPORT =
	"Please acknowledge the travel arrangement before proceeding";
/** The portal's lock-window reason, which leads Approve's and is all Decline has. */
const reasonLocked = (days: number) =>
	`${APPROVAL_TEXT.expiryWarning(days)} ${APPROVAL_TEXT.contactCollege}`;
/** The sample student's given name, for the medical reason. */
export const SAMPLE_GIVEN_NAME = "Sample";

const timeShort = (d: Date): string =>
	new Intl.DateTimeFormat("en-AU", {
		hour: "numeric",
		minute: "numeric",
		timeZone: "Australia/Sydney",
	}).format(d);

const validDate = (iso: string | undefined): Date | null => {
	if (!iso) return null;
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * `ownerName` is passed in, not derived here: `supervisorNames` lives in
 * eventOwner.ts, whose extensionless type import Node's test runner cannot
 * resolve, so the component reads it and this stays testable.
 */
export function buildEventModalModel(
	event: EventDoc,
	now: Date = new Date(),
	ownerName: string | null = null,
): EventModalModel {
	const base = buildApprovalCardModel(event, now);
	/* The preview shows the event BEFORE approval: the open, undecided state a
	   carer meets. An event dated in the past (a test event, say) would otherwise
	   draw no buttons, no dietary link and a closed lock window, which is the
	   portal's view of an event that has started and not what is being checked. */
	const card = base.past ? { ...base, past: false, expiryWarning: false } : base;
	const start = validDate(event.StartDateTime);
	const end = validDate(event.EndDateTime);

	const short = trimmed(event.Description?.Short);
	const long = trimmed(event.Description?.Long);
	const showLong = long !== "" && long !== short;
	const body = showLong ? long : short;
	const notes = trimmed(event.AdditionalNotes);

	/* The reason follows the rows the preview draws, in the portal's order: the
	   lock window first, then the checks. Decline needs only an open window. */
	const locked = card.expiryWarning ? reasonLocked(card.lockDays ?? 0) : "";
	const disabledReason = locked
		? locked
		: card.medicalRow
		? REASON_MEDICAL(SAMPLE_GIVEN_NAME)
		: card.termsHtml
			? REASON_TERMS
			: card.transportAck
				? REASON_TRANSPORT
				: "";

	return {
		...card,
		ownerName: trimmed(ownerName) || null,
		timeLabel: start
			? timeShort(start) + (end ? ` - ${timeShort(end)}` : "")
			: null,
		bodyHtml: body && !isEmptyRichText(body) ? body : null,
		additionalNotesHtml: notes && !isEmptyRichText(notes) ? notes : null,
		disabledReason,
		declineDisabledReason: locked,
	};
}
